import {
  Button,
  Container,
  Group,
  Modal,
  Paper,
  PasswordInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";

import { useCurrentUser, useLogoutAll, useUpdateMe } from "@/lib/auth/hooks";

type ChangePasswordFormValues = {
  current_password: string;
  new_password: string;
  confirm: string;
};

type LogoutAllFormValues = {
  current_password: string;
};

/** Admin settings: change password and sign out of all sessions. */
export function SettingsPage() {
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const updateMe = useUpdateMe();
  const logoutAll = useLogoutAll();
  const [logoutModalOpened, { open: openLogoutModal, close: closeLogoutModal }] =
    useDisclosure(false);

  const passwordForm = useForm<ChangePasswordFormValues>({
    initialValues: {
      current_password: "",
      new_password: "",
      confirm: "",
    },
    validate: {
      current_password: (value) => (value.length > 0 ? null : "Current password is required"),
      new_password: (value) =>
        value.length >= 8 ? null : "New password must be at least 8 characters",
      confirm: (value, values) => (value === values.new_password ? null : "Passwords do not match"),
    },
  });

  const logoutAllForm = useForm<LogoutAllFormValues>({
    initialValues: {
      current_password: "",
    },
    validate: {
      current_password: (value) => (value.length > 0 ? null : "Current password is required"),
    },
  });

  const handlePasswordSubmit = passwordForm.onSubmit(async (values) => {
    try {
      const updated = await updateMe.mutateAsync({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      passwordForm.reset();
      const revoked = updated.revoked_other_sessions;
      const message =
        revoked != null && revoked > 0
          ? `Password updated. ${revoked} other session${revoked === 1 ? "" : "s"} signed out.`
          : "Password updated successfully.";
      notifications.show({ title: "Password updated", message, color: "green" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Password update failed.";
      passwordForm.setFieldError("current_password", message);
      notifications.show({ title: "Password update failed", message, color: "red" });
    }
  });

  const handleLogoutAllSubmit = logoutAllForm.onSubmit(async (values) => {
    try {
      const result = await logoutAll.mutateAsync({ current_password: values.current_password });
      closeLogoutModal();
      logoutAllForm.reset();
      const revoked = result.revoked_sessions;
      notifications.show({
        title: "Signed out everywhere",
        message:
          revoked > 0
            ? `${revoked} session${revoked === 1 ? "" : "s"} revoked. Sign in again to continue.`
            : "All sessions revoked. Sign in again to continue.",
        color: "green",
      });
      navigate("/login", { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Sign out failed.";
      logoutAllForm.setFieldError("current_password", message);
      notifications.show({ title: "Sign out failed", message, color: "red" });
    }
  });

  const handleOpenLogoutModal = () => {
    logoutAllForm.reset();
    openLogoutModal();
  };

  return (
    <Container size="sm" py="md">
      <Stack gap="lg">
        <div>
          <Title order={2}>Settings</Title>
          {user ? (
            <Text c="dimmed" size="sm" mt={4}>
              Signed in as {user.email}
            </Text>
          ) : null}
        </div>

        <Paper withBorder p="lg" radius="md">
          <form onSubmit={handlePasswordSubmit}>
            <Stack gap="md">
              <div>
                <Title order={4}>Change password</Title>
                <Text c="dimmed" size="sm" mt={4}>
                  Enter your current password and choose a new one. Other active sessions will be
                  signed out automatically.
                </Text>
              </div>
              <PasswordInput
                label="Current password"
                autoComplete="current-password"
                required
                {...passwordForm.getInputProps("current_password")}
              />
              <PasswordInput
                label="New password"
                autoComplete="new-password"
                required
                {...passwordForm.getInputProps("new_password")}
              />
              <PasswordInput
                label="Confirm new password"
                autoComplete="new-password"
                required
                {...passwordForm.getInputProps("confirm")}
              />
              <Group justify="flex-end">
                <Button type="submit" loading={updateMe.isPending}>
                  Update password
                </Button>
              </Group>
            </Stack>
          </form>
        </Paper>

        <Paper withBorder p="lg" radius="md">
          <Stack gap="md">
            <div>
              <Title order={4}>Sign out everywhere</Title>
              <Text c="dimmed" size="sm" mt={4}>
                Revoke every active session for your account, including this browser. You will need
                to sign in again on all devices.
              </Text>
            </div>
            <Group justify="flex-start">
              <Button color="red" variant="light" onClick={handleOpenLogoutModal}>
                Sign out of all sessions
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Stack>

      <Modal
        opened={logoutModalOpened}
        onClose={closeLogoutModal}
        title="Sign out of all sessions?"
        centered
      >
        <form onSubmit={handleLogoutAllSubmit}>
          <Stack gap="md">
            <Text size="sm">
              This signs you out on every device, including this one. Enter your current password to
              confirm.
            </Text>
            <PasswordInput
              label="Current password"
              autoComplete="current-password"
              required
              {...logoutAllForm.getInputProps("current_password")}
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={closeLogoutModal} disabled={logoutAll.isPending}>
                Cancel
              </Button>
              <Button type="submit" color="red" loading={logoutAll.isPending}>
                Sign out everywhere
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Container>
  );
}
